import * as React from 'react';
import { OTPInput, REGEXP_ONLY_DIGITS, SlotProps } from 'input-otp';
import { cn } from './utils';

const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(({ className, containerClassName, ...props }, ref) => (
  <OTPInput
    ref={ref}
    containerClassName={cn(
      'group flex items-center gap-3 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50',
      containerClassName,
    )}
    className={cn('flex items-center gap-2', className)}
    {...props}
  />
));
InputOTP.displayName = 'InputOTP';

const InputOTPGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center gap-2', className)} {...props} />
  ),
);
InputOTPGroup.displayName = 'InputOTPGroup';

const InputOTPSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('mx-2 flex items-center justify-center text-muted-foreground', className)}
      {...props}
    />
  ),
);
InputOTPSeparator.displayName = 'InputOTPSeparator';

const InputOTPSlot = React.forwardRef<
  HTMLDivElement,
  SlotProps & React.HTMLAttributes<HTMLDivElement>
>(({ char, placeholderChar, hasFakeCaret, isActive, className, ...props }, ref) => (
  <div
    ref={ref}
    data-state={isActive ? 'active' : undefined}
    className={cn(
      'relative flex h-14 w-12 items-center justify-center rounded-xl border border-input bg-background text-2xl font-semibold text-foreground shadow-sm transition-all',
      'group-hover:border-foreground/20 group-focus-within:border-primary group-focus-within:ring-2 group-focus-within:ring-primary/20',
      'data-[state=active]:border-primary data-[state=active]:ring-2 data-[state=active]:ring-primary/30',
      'data-[state=inactive]:text-muted-foreground',
      className,
    )}
    {...props}
  >
    <span className="pointer-events-none">{char ?? placeholderChar ?? '\u00a0'}</span>
    {hasFakeCaret ? (
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <span className="h-6 w-px animate-pulse bg-foreground" />
      </span>
    ) : null}
  </div>
));
InputOTPSlot.displayName = 'InputOTPSlot';

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, REGEXP_ONLY_DIGITS };
